import { Box, Button, Stack, Text } from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'

/**
 * Signing in, from the sidebar.
 *
 * This is not the call to sign *up* — that lives beside the brief, where it can
 * say what signing up buys and ask what the buyer and their organisation are
 * called. This is the returning person's door: they already belong to a buyer
 * and already have a name, so there is nothing to ask them first.
 *
 * It offers the door and nothing more. The boundary's own form used to open in
 * a dialog from here, and that stopped being tenable the moment a guarded
 * address also had to offer a way in: two presentations of the boundary are two
 * sets of failure handling, two answers to where somebody lands afterwards, and
 * two places to remember that signing in claims the brief this browser holds.
 * `/signin` is the one place, and this points at it.
 *
 * Both ends still arrive at the same claim — `/signin` calls `useSignup` with
 * the boundary's token exactly as the dialog did — so the promise made here
 * about the held brief remains true.
 */
export function SignIn() {
  return (
    <Box bg="sidebar.hover" borderRadius="l3" p="4">
      <Stack gap="3" align="stretch">
        <Text fontSize="sm" fontWeight="semibold" letterSpacing="heading" color="sidebar.fg">
          Already have an account?
        </Text>
        <Text fontSize="xs" lineHeight="body" color="sidebar.fg" opacity="0.75">
          Sign in and the brief this browser is holding comes with you.
        </Text>

        <Button
          asChild
          size="sm"
          w="full"
          bg="sidebar.solid"
          color="sidebar.bg"
          borderRadius="l2"
          fontWeight="medium"
          letterSpacing="ui"
          _hover={signInHover}
        >
          <Link to="/signin">Sign in</Link>
        </Button>
      </Stack>
    </Box>
  )
}

// Hoisted: an object literal in JSX would be a new prop on every render.
const signInHover = { bg: 'sidebar.solidHover' }
